import React, { useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { HttpMethod, SimpleRule } from "../../types";
import MaskedHeaderField from "../../components/masked-header-field/MaskedHeaderField";
import styles from "./RuleForm.module.css";

const HTTP_METHODS: HttpMethod[] = ["GET", "POST", "PUT", "PATCH", "DELETE"];
const EXTRA_FIELD_TYPES = ["string", "number", "boolean"] as const;
type ExtraFieldType = (typeof EXTRA_FIELD_TYPES)[number];

const headerRowSchema = z.object({
  key: z.string().min(1, "Chiave obbligatoria"),
  value: z.string(),
});

const extraFieldRowSchema = z.object({
  key: z.string().min(1, "Chiave obbligatoria"),
  type: z.enum(EXTRA_FIELD_TYPES),
  value: z.string().min(1, "Valore obbligatorio"),
});

const mappingRowSchema = z.object({
  source: z.string().min(1, "Campo obbligatorio"),
  target: z.string().min(1, "Campo obbligatorio"),
});

const formSchema = z
  .object({
    displayName: z
      .string()
      .min(1, "Campo obbligatorio")
      .max(100, "Massimo 100 caratteri"),
    active: z.boolean(),
    url: z.string().min(1, "URL obbligatorio"),
    method: z.enum(HTTP_METHODS as [HttpMethod, ...HttpMethod[]]),
    timeout_ms: z.coerce
      .number()
      .int()
      .min(1000, "Minimo 1000ms")
      .max(30000, "Massimo 30000ms"),
    headers: z.array(headerRowSchema),
    extra_fields: z.array(extraFieldRowSchema),
    mapping: z.array(mappingRowSchema),
  })
  .superRefine((data, ctx) => {
    try {
      const u = new URL(data.url);
      if (u.protocol !== "https:" && u.hostname !== "localhost") {
        ctx.addIssue({
          code: "custom",
          path: ["url"],
          message: "Solo URL https:// sono ammessi",
        });
      }
    } catch {
      ctx.addIssue({
        code: "custom",
        path: ["url"],
        message: "URL non valido",
      });
    }

    const seen = new Map<string, number>();
    data.headers.forEach((h, i) => {
      const k = h.key.trim().toLowerCase();
      if (!k) return;
      if (seen.has(k)) {
        ctx.addIssue({
          code: "custom",
          path: [`headers.${i}.key`],
          message: "Header duplicato (case-insensitive)",
        });
      } else {
        seen.set(k, i);
      }
    });

    data.extra_fields.forEach((row, i) => {
      if (row.type === "number" && Number.isNaN(Number(row.value))) {
        ctx.addIssue({
          code: "custom",
          path: [`extra_fields.${i}.value`],
          message: "Valore numerico non valido",
        });
      }
      if (
        row.type === "boolean" &&
        row.value !== "true" &&
        row.value !== "false"
      ) {
        ctx.addIssue({
          code: "custom",
          path: [`extra_fields.${i}.value`],
          message: "Usa true o false",
        });
      }
    });
  });

export type SimpleRuleFormData = z.infer<typeof formSchema>;

export function simpleRuleToFormData(rule: SimpleRule): SimpleRuleFormData {
  return {
    displayName: rule.displayName,
    active: rule.active,
    url: rule.destination.url,
    method: rule.destination.method,
    timeout_ms: rule.destination.timeout_ms ?? 15000,
    headers: rule.destination.headers
      ? Object.entries(rule.destination.headers).map(([key, value]) => ({
          key,
          value,
        }))
      : [],
    extra_fields: rule.destination.extra_fields
      ? Object.entries(rule.destination.extra_fields).map(([key, value]) => {
          const type: ExtraFieldType =
            typeof value === "number"
              ? "number"
              : typeof value === "boolean"
              ? "boolean"
              : "string";
          return { key, type, value: String(value) };
        })
      : [],
    mapping: rule.mapping
      ? Object.entries(rule.mapping).map(([source, target]) => ({
          source,
          target,
        }))
      : [],
  };
}

export function simpleFormDataToPayload(
  data: SimpleRuleFormData,
  opts: { forUpdate?: boolean } = {}
) {
  const { forUpdate = false } = opts;

  const headers: Record<string, string> = {};
  data.headers.forEach(({ key, value }) => {
    const k = key.trim();
    if (k) headers[k] = value;
  });

  const extra_fields: Record<string, string | number | boolean> = {};
  data.extra_fields.forEach((row) => {
    const k = row.key.trim();
    if (!k) return;
    if (row.type === "number") extra_fields[k] = Number(row.value);
    else if (row.type === "boolean") extra_fields[k] = row.value === "true";
    else extra_fields[k] = row.value;
  });

  const mapping: Record<string, string> = {};
  data.mapping.forEach((row) => {
    const s = row.source.trim();
    const t = row.target.trim();
    if (s && t) mapping[s] = t;
  });

  const destination: Record<string, unknown> = {
    url: data.url.trim(),
    method: data.method,
    timeout_ms: data.timeout_ms,
  };
  if (forUpdate || Object.keys(headers).length > 0) {
    destination.headers = headers;
  }
  if (forUpdate || Object.keys(extra_fields).length > 0) {
    destination.extra_fields = extra_fields;
  }

  const payload: Record<string, unknown> = {
    type: "simple",
    displayName: data.displayName.trim(),
    active: data.active,
    destination,
  };

  if (forUpdate || Object.keys(mapping).length > 0) {
    payload.mapping = mapping;
  }

  return payload;
}

interface Props {
  defaultValues?: SimpleRuleFormData;
  onSubmit: (data: SimpleRuleFormData) => Promise<void>;
  isLoading: boolean;
  submitLabel: string;
  submitError?: string | null;
}

export default function SimpleRuleForm({
  defaultValues,
  onSubmit,
  isLoading,
  submitLabel,
  submitError,
}: Props) {
  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<SimpleRuleFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: defaultValues ?? {
      displayName: "",
      active: true,
      url: "",
      method: "POST",
      timeout_ms: 15000,
      headers: [],
      extra_fields: [],
      mapping: [],
    },
  });

  useEffect(() => {
    if (defaultValues) reset(defaultValues);
  }, [defaultValues, reset]);

  const {
    fields: headerFields,
    append: appendHeader,
    remove: removeHeader,
  } = useFieldArray({ control, name: "headers" });

  const {
    fields: extraFields,
    append: appendExtra,
    remove: removeExtra,
  } = useFieldArray({ control, name: "extra_fields" });

  const {
    fields: mappingFields,
    append: appendMapping,
    remove: removeMapping,
  } = useFieldArray({ control, name: "mapping" });

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      {/* Info base */}
      <Box className={styles.section}>
        <Typography variant="subtitle1" className={styles.sectionTitle}>
          Informazioni base
        </Typography>
        <Box className={styles.row}>
          <Controller
            name="displayName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Nome regola"
                required
                fullWidth
                inputProps={{ maxLength: 100 }}
                error={!!errors.displayName}
                helperText={errors.displayName?.message}
              />
            )}
          />
        </Box>
        <Controller
          name="active"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Switch checked={field.value} onChange={field.onChange} />
              }
              label="Regola attiva"
            />
          )}
        />
      </Box>

      <Divider />

      {/* Destinazione */}
      <Box className={styles.section}>
        <Typography variant="subtitle1" className={styles.sectionTitle}>
          Destinazione
        </Typography>

        <Box className={styles.rowSplit} sx={{ mb: 2 }}>
          <Controller
            name="url"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="URL"
                required
                fullWidth
                placeholder="https://partner.example.com/leads"
                error={!!errors.url}
                helperText={errors.url?.message}
                sx={{ flex: 2, minWidth: 280 }}
              />
            )}
          />
          <Controller
            name="method"
            control={control}
            render={({ field }) => (
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>HTTP Method</InputLabel>
                <Select {...field} label="HTTP Method">
                  {HTTP_METHODS.map((m) => (
                    <MenuItem key={m} value={m}>
                      {m}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
          <Controller
            name="timeout_ms"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Timeout (ms)"
                type="number"
                inputProps={{ min: 1000, max: 30000, step: 500 }}
                error={!!errors.timeout_ms}
                helperText={errors.timeout_ms?.message ?? "1000–30000"}
                sx={{ minWidth: 140 }}
              />
            )}
          />
        </Box>

        {/* Headers */}
        <Typography variant="body2" fontWeight={600} sx={{ mt: 2, mb: 0.5 }}>
          Header HTTP (opzionale)
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
          Le chiavi sensibili (Authorization, token, ecc.) sono mascherate di default.
        </Typography>

        {headerFields.map((headerField, index) => {
          const headerName = watch(`headers.${index}.key`) ?? "";
          return (
            <Box key={headerField.id} className={styles.headerRow}>
              <Controller
                name={`headers.${index}.key`}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Header"
                    size="small"
                    placeholder="X-Api-Key"
                    error={!!errors.headers?.[index]?.key}
                    helperText={errors.headers?.[index]?.key?.message}
                    sx={{ flex: 1 }}
                  />
                )}
              />
              <Box sx={{ flex: 2 }}>
                <Controller
                  name={`headers.${index}.value`}
                  control={control}
                  render={({ field }) => (
                    <MaskedHeaderField
                      headerName={headerName}
                      value={field.value}
                      onChange={field.onChange}
                      label="Valore"
                      size="small"
                      fullWidth
                    />
                  )}
                />
              </Box>
              <IconButton
                color="error"
                onClick={() => removeHeader(index)}
                title="Rimuovi header"
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          );
        })}

        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          size="small"
          sx={{ mt: 1, mb: 3 }}
          onClick={() => appendHeader({ key: "", value: "" })}
        >
          Aggiungi header
        </Button>

        {/* Extra fields */}
        <Typography variant="body2" fontWeight={600} sx={{ mt: 1, mb: 0.5 }}>
          Campi extra (opzionale)
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
          Valori costanti iniettati nel payload outbound. Solo valori statici.
        </Typography>

        {extraFields.length === 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontStyle: "italic", mb: 1 }}>
            Nessun campo extra configurato.
          </Typography>
        )}

        {extraFields.map((extraField, index) => {
          const type = watch(`extra_fields.${index}.type`);
          return (
            <Box key={extraField.id} className={styles.extraRow}>
              <Controller
                name={`extra_fields.${index}.key`}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Chiave"
                    size="small"
                    placeholder="source"
                    inputProps={{ style: { fontFamily: "monospace" } }}
                    error={!!errors.extra_fields?.[index]?.key}
                    helperText={errors.extra_fields?.[index]?.key?.message}
                    sx={{ flex: 1 }}
                  />
                )}
              />
              <Controller
                name={`extra_fields.${index}.type`}
                control={control}
                render={({ field }) => (
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Tipo</InputLabel>
                    <Select {...field} label="Tipo">
                      <MenuItem value="string">string</MenuItem>
                      <MenuItem value="number">number</MenuItem>
                      <MenuItem value="boolean">boolean</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
              <Controller
                name={`extra_fields.${index}.value`}
                control={control}
                render={({ field }) =>
                  type === "boolean" ? (
                    <FormControl size="small" sx={{ flex: 1 }}>
                      <InputLabel>Valore</InputLabel>
                      <Select {...field} label="Valore">
                        <MenuItem value="true">true</MenuItem>
                        <MenuItem value="false">false</MenuItem>
                      </Select>
                    </FormControl>
                  ) : (
                    <TextField
                      {...field}
                      label="Valore"
                      size="small"
                      type={type === "number" ? "number" : "text"}
                      error={!!errors.extra_fields?.[index]?.value}
                      helperText={errors.extra_fields?.[index]?.value?.message}
                      sx={{ flex: 1 }}
                    />
                  )
                }
              />
              <IconButton
                color="error"
                onClick={() => removeExtra(index)}
                title="Rimuovi campo"
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          );
        })}

        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          size="small"
          sx={{ mt: 1 }}
          onClick={() =>
            appendExtra({ key: "", type: "string", value: "" })
          }
        >
          Aggiungi campo extra
        </Button>
      </Box>

      <Divider />

      {/* Mapping */}
      <Box className={styles.section}>
        <Typography variant="subtitle1" className={styles.sectionTitle}>
          Field Mapping (opzionale)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Rimappa i campi del payload sorgente (dot-notation) verso i nomi attesi dal partner.
        </Typography>

        {mappingFields.length === 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontStyle: "italic", mb: 1 }}>
            Nessun mapping configurato — verrà inoltrato il payload completo.
          </Typography>
        )}

        {mappingFields.map((mappingField, index) => (
          <Box key={mappingField.id} className={styles.headerRow}>
            <Controller
              name={`mapping.${index}.source`}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Campo sorgente"
                  size="small"
                  placeholder="data.cellulare"
                  inputProps={{ style: { fontFamily: "monospace" } }}
                  error={!!errors.mapping?.[index]?.source}
                  helperText={errors.mapping?.[index]?.source?.message}
                  sx={{ flex: 1 }}
                />
              )}
            />
            <TextField
              value="→"
              size="small"
              disabled
              sx={{
                width: 40,
                "& .MuiInputBase-input": { textAlign: "center", p: 1 },
              }}
            />
            <Controller
              name={`mapping.${index}.target`}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Campo destinazione"
                  size="small"
                  placeholder="phone"
                  inputProps={{ style: { fontFamily: "monospace" } }}
                  error={!!errors.mapping?.[index]?.target}
                  helperText={errors.mapping?.[index]?.target?.message}
                  sx={{ flex: 1 }}
                />
              )}
            />
            <IconButton
              color="error"
              onClick={() => removeMapping(index)}
              title="Rimuovi mapping"
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        ))}

        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          size="small"
          sx={{ mt: 1 }}
          onClick={() => appendMapping({ source: "", target: "" })}
        >
          Aggiungi mapping
        </Button>
      </Box>

      {submitError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {submitError}
        </Alert>
      )}

      <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
        <Button type="submit" variant="contained" disabled={isLoading}>
          {isLoading ? "Salvataggio..." : submitLabel}
        </Button>
      </Box>
    </form>
  );
}
